/**
 * Helpers d'affichage des prix produits boutique (EUROS, décimales fixes —
 * formatCurrency canonique via @donoor/utils).
 */
import { formatCurrency } from "@donoor/utils";
import { LOCALE_INTL, type Langue } from "~/utils/langue";
import type {
  ShopCartLineView,
  ShopProduct,
  ShopShippingMethod,
} from "~/types/shop";
import {
  getVariantRemaining,
  isVariantSoldOut,
} from "~/composables/useShopCart";

/** Variantes actives d'un produit. */
/**
 * Traduction hors composant (fonctions pures appelées à l'affichage) : via
 * l'instance i18n de Nuxt, résolue à chaque appel → suit la langue de la page
 * (cf. docs/architecture/i18n.md).
 */
function t(cle: string, params: Record<string, unknown> = {}): string {
  return useNuxtApp().$i18n.t(cle, params);
}

/** Montant dans la langue de la page (même résolution, au moment de l'appel). */
function montant(n: number): string {
  const langue = useNuxtApp().$i18n.locale.value as Langue;
  return formatCurrency(n, { locale: LOCALE_INTL[langue] ?? LOCALE_INTL.fr });
}

function activeVariants(p: ShopProduct) {
  return (p.variants || []).filter((v) => v.is_active);
}

/** Prix minimum d'un produit toutes variantes confondues (EUROS). */
export function shopProductMinPrice(p: ShopProduct): number {
  if (p.pricing === "free") return 0;
  if (p.pricing === "flexible") return Number(p.min_price || 0);
  const base = Number(p.price || 0);
  const overrides = activeVariants(p)
    .filter((v) => v.price_override != null)
    .map((v) => Number(v.price_override));
  return overrides.length > 0 ? Math.min(base, ...overrides) : base;
}

/** Les variantes ont-elles des prix différents entre elles ? */
export function shopProductHasPriceRange(p: ShopProduct): boolean {
  if (p.pricing !== "fixed") return false;
  const prices = activeVariants(p).map((v) =>
    Number(v.price_override ?? p.price ?? 0),
  );
  if (prices.length <= 1) return false;
  return Math.min(...prices) !== Math.max(...prices);
}

/**
 * Libellé de prix pour la grille produits :
 *  - fixe, variantes à prix différents → « À partir de X € »
 *  - fixe → « X € » / « Gratuit »
 *  - libre → « Prix libre » (+ minimum)
 *  - gratuit → « Gratuit »
 */
export function shopProductPriceLabel(p: ShopProduct): string {
  if (p.pricing === "free") return t("boutique.commun.gratuit");
  if (p.pricing === "flexible") {
    const min = Number(p.min_price || 0);
    return min > 0 ? t("boutique.tarifs.prixLibreMin", { montant: montant(min) }) : t("boutique.productSelection.prixLibre");
  }
  const min = shopProductMinPrice(p);
  if (min <= 0 && !shopProductHasPriceRange(p)) return t("boutique.commun.gratuit");
  return shopProductHasPriceRange(p)
    ? t("boutique.productSelection.aPartirDe", { montant: montant(min) })
    : montant(min);
}

/**
 * Stock restant total d'un produit (somme des variantes actives).
 * `null` = illimité (au moins une variante sans limite).
 */
export function shopProductTotalRemaining(p: ShopProduct): number | null {
  const variants = activeVariants(p);
  if (variants.length === 0) return null;
  let total = 0;
  for (const v of variants) {
    const remaining = getVariantRemaining(v);
    if (remaining === null) return null;
    total += remaining;
  }
  return total;
}

/** Un produit est épuisé si TOUTES ses variantes actives le sont. */
export function shopProductSoldOut(p: ShopProduct): boolean {
  const variants = activeVariants(p);
  if (variants.length === 0) return false;
  return variants.every((v) => isVariantSoldOut(v));
}

// ==========================================
// LIVRAISON — MIROIR EXACT DU SERVEUR
// ==========================================
//
// ⚠️ CONTRAT DE SYNCHRONISATION. Ce bloc réplique DEUX calculs serveur :
//   1. la BASE du franco  → `priceOrderLines` (shopOrderPricing.ts, bloc
//      « Marchandise PHYSIQUE nette, au prorata de la remise ») ;
//   2. la RÉSOLUTION du frais → `ShopShippingService.resolveShippingFee`.
// Toute modification de l'un des deux côtés DOIT être répercutée sur l'autre :
// c'est le serveur qui débite, un écart produit un « Payer X € » (et une sheet
// Apple/Google Pay) ≠ du montant réellement prélevé.

/**
 * MIROIR EXACT de la base du franco calculée par `priceOrderLines`
 * (apps/api.donoor.org/src/shop/services/shopOrderPricing.ts, bloc
 * « Marchandise PHYSIQUE nette, au prorata de la remise »).
 *
 * ⚠️ NE JAMAIS passer le panier ENTIER à `resolveShopShippingFee` : un produit
 * `digital` (PDF, fichier) ne coûte ni timbre ni carton, donc il ne doit PAS
 * faire atteindre un « offert dès 50 € ». Le serveur calcule le franco sur la
 * seule MARCHANDISE PHYSIQUE, remise promo répartie AU PRORATA. Utiliser une
 * autre base ferait diverger le « Payer X € » affiché du montant réellement
 * débité sur un panier mixte (numérique + physique).
 *
 * Formule serveur répliquée à l'identique :
 *   physicalSubtotal = Σ lineTotal des lignes NON digitales           (round2)
 *   physicalNet      = subtotal > 0
 *                        ? round2(physicalSubtotal × (netAmount / subtotal))
 *                        : physicalSubtotal
 * où `subtotal` = Σ de TOUTES les lignes et `netAmount` = subtotal − remise.
 *
 * `product_type` absent (données antérieures à la migration 166) ⇒ `physical`,
 * exactement comme le DEFAULT en base et le test `!== "digital"` du serveur.
 *
 * @param cartLines lignes du panier enrichies (produit résolu)
 * @param totalAfterDiscount marchandise nette du panier ENTIER en EUROS
 * @returns marchandise PHYSIQUE nette en EUROS — la base du franco
 */
export function shopPhysicalNetAmount(
  cartLines: ShopCartLineView[],
  totalAfterDiscount: number,
): number {
  const subtotal = shopRound2(
    cartLines.reduce((sum, line) => sum + (Number(line.lineTotal) || 0), 0),
  );
  const physicalSubtotal = shopRound2(
    cartLines
      .filter((line) => line.product?.product_type !== "digital")
      .reduce((sum, line) => sum + (Number(line.lineTotal) || 0), 0),
  );
  const netAmount = Math.max(0, shopRound2(Number(totalAfterDiscount) || 0));
  if (subtotal <= 0) return physicalSubtotal;
  return shopRound2(physicalSubtotal * (netAmount / subtotal));
}

/** Le panier contient-il au moins une ligne PHYSIQUE ? (miroir `hasPhysicalLine`) */
export function shopHasPhysicalLine(cartLines: ShopCartLineView[]): boolean {
  return cartLines.some((line) => line.product?.product_type !== "digital");
}

/**
 * MIROIR EXACT de `ShopShippingService.resolveShippingFee` (API).
 *
 * Frais de port dus pour un mode, en EUROS : `0` si le franco est atteint
 * (marchandise PHYSIQUE nette ≥ `free_above`), sinon le prix du mode. Le
 * serveur recalcule de toute façon — cette fonction sert à afficher le bon
 * montant AVANT l'appel (récap, sidebar, sheet wallet).
 *
 * @param method mode choisi (null = aucun mode → 0)
 * @param physicalNetAmount marchandise PHYSIQUE nette en EUROS
 *   (⚠️ `shopPhysicalNetAmount`, PAS le total du panier — cf. son doc)
 */
export function resolveShopShippingFee(
  method: ShopShippingMethod | null | undefined,
  physicalNetAmount: number,
): number {
  if (!method) return 0;
  const price = Math.max(0, Number(method.price) || 0);
  if (price <= 0) return 0;

  const freeAbove =
    method.free_above === null || method.free_above === undefined
      ? null
      : Math.max(0, Number(method.free_above) || 0);

  const basket = Math.max(0, shopRound2(Number(physicalNetAmount) || 0));
  // Comparaison INCLUSIVE : « offert dès X € » (identique au serveur).
  if (freeAbove !== null && basket >= freeAbove) return 0;
  return price;
}

/**
 * Libellé de prix d'un mode au checkout : « Offert », « 5,90 € ».
 * @param physicalNetAmount marchandise PHYSIQUE nette (`shopPhysicalNetAmount`)
 */
export function shopShippingPriceLabel(
  method: ShopShippingMethod,
  physicalNetAmount: number,
): string {
  const fee = resolveShopShippingFee(method, physicalNetAmount);
  return fee > 0 ? montant(fee) : t("boutique.commun.offert");
}

/**
 * Argument commercial affiché sous un mode payant dont le franco n'est PAS
 * encore atteint : « Offert dès 50 € » (incite à compléter le panier).
 * Null si le mode est déjà gratuit ou le franco déjà atteint.
 *
 * @param physicalNetAmount marchandise PHYSIQUE nette (`shopPhysicalNetAmount`)
 */
export function shopShippingFreeAboveHint(
  method: ShopShippingMethod,
  physicalNetAmount: number,
): string | null {
  const freeAbove =
    method.free_above === null || method.free_above === undefined
      ? null
      : Number(method.free_above);
  if (freeAbove === null || freeAbove <= 0) return null;
  if (Math.max(0, Number(method.price) || 0) <= 0) return null;
  if (resolveShopShippingFee(method, physicalNetAmount) === 0) return null;
  return t("boutique.tarifs.offertDes", { montant: montant(freeAbove) });
}

/** « Livraison estimée sous 2 à 4 jours » — null si non renseigné. */
export function shopShippingDelayLabel(
  method: ShopShippingMethod,
): string | null {
  const min = method.estimated_days_min ?? null;
  const max = method.estimated_days_max ?? null;
  if (min === null && max === null) return null;
  if (min !== null && max !== null && min !== max) return t("boutique.tarifs.joursIntervalle", { min, max });
  const days = Number(min ?? max);
  const n = days <= 1 ? 1 : days;
  return useNuxtApp().$i18n.t("boutique.tarifs.jours", { n }, n);
}

// ==========================================
// FRAIS — MIROIR EXACT DU SERVEUR
// ==========================================

/** Mêmes constantes que `publicShopController` (et la billetterie). */
const STRIPE_RATE = 0.015;
const STRIPE_FIXED_FEE = 0.25;

/** Barème d'un prestataire de paiement : taux en décimal + part fixe en €. */
export interface BaremePsp {
  taux: number;
  fixe: number;
}

/** Barème Stripe — défaut historique, et repli quand PayPal est inconnu. */
export const BAREME_STRIPE: BaremePsp = {
  taux: STRIPE_RATE,
  fixe: STRIPE_FIXED_FEE,
};

/**
 * Barème du prestataire RÉELLEMENT utilisé — MIROIR de `baremePsp` (serveur).
 *
 * 🔴 POURQUOI (Karim 2026-09-22). Appliquer le barème Stripe à un paiement
 * PayPal faisait sous-estimer les frais à couvrir : sur une asso à
 * 1,2 % + 0,35 €, un article à 15 € « frais couverts » ne rapportait que
 * 14,94 €. Repli sur Stripe si le tarif PayPal appris est absent ou aberrant.
 */
export function baremePspBoutique(
  provider: "stripe" | "paypal",
  paypalRates: { fee_percentage?: number; fee_fixed?: number } | null | undefined,
): BaremePsp {
  if (provider === "paypal" && paypalRates) {
    const taux = Number(paypalRates.fee_percentage) / 100;
    const fixe = Number(paypalRates.fee_fixed);
    if (taux > 0 && taux < 1 && Number.isFinite(fixe) && fixe >= 0) {
      return { taux, fixe };
    }
  }
  return BAREME_STRIPE;
}

/** Arrondi 2 décimales — MÊME arrondi que le serveur (Math.round, pas ceil). */
export function shopRound2(n: number): number {
  return Math.round(n * 100) / 100;
}

export interface ShopFeesBreakdown {
  /** Commission Donoor en EUROS (taux × marchandise NETTE, don EXCLU). */
  donoorFees: number;
  /** Frais Stripe pris en charge par l'acheteur en EUROS (0 si non couverts). */
  stripeFees: number;
  /** Total réellement débité à l'acheteur en EUROS. */
  totalCharged: number;
}

/**
 * MIROIR EXACT de `calculateFeesForShop` (apps/api.donoor.org/src/shop/
 * controllers/publicShopController.ts) — c'est le SERVEUR qui débite : toute
 * modification doit être faite DES DEUX CÔTÉS, sinon le « Payer X € » et la
 * sheet Apple/Google Pay affichent un montant ≠ montant débité.
 *
 * Règles serveur :
 *  - assiette commission Donoor = marchandise NETTE (subtotal − remise),
 *    le don additionnel ET les frais de port sont EXCLUS (le don revient
 *    intégralement à l'asso ; le port rembourse un coût réel — timbre,
 *    carton — donc n'est pas du CA commissionnable) ;
 *  - les frais de port entrent en revanche dans le TOTAL PAYÉ (`orgBase`) ;
 *  - arrondis Math.round à 2 décimales (round2), jamais Math.ceil ;
 *  - frais non couverts → l'org les absorbe, total = net + port + don ;
 *  - le SOUTIEN DONOOR (`donoorTipEuros`) s'ajoute toujours au total payé,
 *    n'est jamais commissionné, et n'entre jamais dans `orgBase` (il ne
 *    revient pas à l'association).
 *
 * @param netAmount marchandise nette en EUROS (subtotal − remise promo)
 * @param donoorFeeRate taux de commission Donoor en POURCENT (ex. 2 pour 2 %)
 * @param includeFees l'acheteur couvre-t-il les frais ?
 * @param donationEuros don additionnel à l'asso en EUROS
 * @param shippingEuros frais de port en EUROS (0 si retrait/franco/aucun mode)
 * @param donoorTipEuros soutien Donoor en EUROS (pourboire, 0 par défaut)
 */
export function computeShopFees(
  netAmount: number,
  donoorFeeRate: number,
  includeFees: boolean,
  donationEuros: number,
  shippingEuros = 0,
  donoorTipEuros = 0,
  bareme: BaremePsp = BAREME_STRIPE,
): ShopFeesBreakdown {
  const orgBase = shopRound2(netAmount + shippingEuros + donationEuros);
  // Commande entièrement gratuite ET sans pourboire : rien à débiter. Un
  // pourboire SEUL reste un paiement réel (produit gratuit + soutien Donoor).
  if (orgBase === 0 && donoorTipEuros === 0) {
    return { donoorFees: 0, stripeFees: 0, totalCharged: 0 };
  }

  const donoorFees = shopRound2(netAmount * (donoorFeeRate / 100));

  if (!includeFees) {
    // L'acheteur ne couvre pas les frais → l'org les absorbe. Le pourboire est
    // TOUJOURS payé en plus : ce n'est pas un frais, c'est un choix de
    // l'acheteur.
    return {
      donoorFees,
      stripeFees: 0,
      totalCharged: shopRound2(orgBase + donoorTipEuros),
    };
  }

  // Frais couverts : le pourboire transite lui aussi par Stripe, donc il entre
  // dans l'assiette du pourcentage bancaire — sinon l'association absorberait
  // les frais d'un montant qui ne lui revient pas.
  const chargedBase = shopRound2(orgBase + donoorTipEuros);
  const baseAmount = chargedBase + donoorFees + bareme.fixe;
  const totalCharged = shopRound2(baseAmount / (1 - bareme.taux));
  const stripeFees = shopRound2(totalCharged - chargedBase - donoorFees);
  return { donoorFees, stripeFees, totalCharged };
}
